import { REGIME_BEHAVIOR, REGIME_NEXT, PHASE, useBoxWidth, niceScale, padRange, pctTick, shortDate, signed } from "./srUtil";

// One fund's position since it bought (% vs similar stocks), with its regime's typical shape and one month ahead.
const FundPathChart = ({ f }) => {
  const [ref, w] = useBoxWidth(480);
  const W = Math.max(260, w), H = 230, X0 = 44, X1 = W - 54, bot = H - 40;
  const p = f.path_pct, n = p.length - 1, total = n + (f.next ? 1 : 0);
  const body = p.slice(1), pmin = Math.min(...body), pmax = Math.max(...body);
  const toPct = (a) => pmin + a * (pmax - pmin);
  const arch = f.archetype.map(toPct), nxt = f.next ? toPct(f.next.value) : null;
  const vals = [...p, ...arch, ...(nxt != null ? [nxt] : [])];
  const sc = niceScale(...padRange(Math.min(0, ...vals), Math.max(0, ...vals)), 4);
  const x = (i) => X0 + (i / Math.max(1, total)) * (X1 - X0), y = (v) => bot - ((v - sc.min) / (sc.max - sc.min)) * (bot - 26);
  const line = (arr, k0) => arr.map((v, i) => `${i ? "L" : "M"}${x(i + k0).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const last = p[n];
  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" data-testid="sr-fund-regime-chart"
        aria-label="This fund's position since it bought, against similar stocks in its sector, next to the regime's typical shape">
        {sc.ticks.map((v) => (
          <g key={v}><line x1={X0} x2={X1} y1={y(v)} y2={y(v)} stroke={v === 0 ? "#5b6675" : "#26303b"} strokeDasharray={v === 0 ? "0" : "2 5"} />
            <text x={X0 - 8} y={y(v) + 4} textAnchor="end" fill="#8e9aa9" fontSize="11">{pctTick(v)}</text></g>
        ))}
        {f.next && <rect x={x(n)} y="20" width={x(n + 1) - x(n)} height={bot - 20} fill="#a0aab8" opacity=".07" />}
        {arch.length > 1 && <path d={line(arch, 1)} stroke="#a0aab8" strokeWidth="2" strokeDasharray="5 5" fill="none" />}
        {nxt != null && <path d={line([arch[arch.length - 1], nxt], n)} stroke="#e6eaf0" strokeWidth="2" strokeDasharray="2 4" fill="none" />}
        <path d={line(p, 0)} stroke="#38bdf8" strokeWidth="3" fill="none" />
        <circle cx={x(n)} cy={y(last)} r="4" fill="#38bdf8" />
        <text x={x(n) + 8} y={y(last) + 4} fill="#38bdf8" fontSize="12" fontWeight="600">{`${signed(last)}%`}</text>
        {p.map((_, i) => <text key={i} x={x(i)} y={bot + 18} textAnchor="middle" fill="#8e9aa9" fontSize="10">{i === 0 ? (f.since_buy ? "Bought" : `${n} mo ago`) : i === n ? "today" : `${i}`}</text>)}
        {f.next && <text x={x(n + 1)} y={bot + 18} textAnchor="middle" fill="#a0aab8" fontSize="10">+1 mo</text>}
        <text x={(X0 + X1) / 2} y={H - 4} textAnchor="middle" fill="#5f6b7a" fontSize="10">{f.since_buy ? "Months since it bought" : `Last ${n} months of the position`}</text>
      </svg>
    </div>
  );
};

export const fundRegimeDetail = (f, r, symbol, buyer, sector) => {
  const same = f.cluster === r.cluster, last = f.path_pct[f.path_pct.length - 1], ph = PHASE[f.group];
  return {
    title: f.name,
    body: (
      <>
        <p>
          <span className={`pill ${ph ? ph[1] : ""}`} data-testid="sr-fund-regime-tag">{f.regime}{ph ? ` · ${ph[0]} phase` : ""}</span>
          {same && <span className="pill">Same regime as {symbol}</span>}
        </p>
        <p>{REGIME_BEHAVIOR[f.cluster]}</p>
        <FundPathChart f={f} />
        <div className="fundlegend"><span style={{ color: "#38bdf8" }}>━━ This position</span><span>┄┄ Typical shape</span>
          {f.next && <span>··· Next month (typical)</span>}</div>
        <div className="fundstats" data-testid="sr-fund-regime-stats">
          <div><b>{shortDate(f.bought, true)}</b><small>Bought (estimated)</small></div>
          <div><b>{f.months} month{f.months === 1 ? "" : "s"}</b><small>Held so far · {f.days} trading days</small></div>
          <div><b style={{ color: last >= 0 ? "#34d399" : "#f87171" }}>{signed(last)}%</b><small>{f.since_buy ? "Against similar stocks since then" : `Against similar stocks, last ${f.path_pct.length - 1} months`}</small></div>
        </div>
        {f.next && <p>If this position stays in this regime, the next month usually {REGIME_NEXT[f.next.direction]}.</p>}
        {buyer?.top_pct != null && <p><b>Track record:</b> top {buyer.top_pct}% in {sector}.</p>}
        <p className="smallnote">The buy date is estimated from the fund’s filings.{f.since_buy ? "" : " For positions held this long, we read the most recent months only."} Performance is measured against similar-sized stocks in
          the same sector. We look one
          month ahead at most.</p>
      </>
    ),
  };
};
